import {
    DocumentTypeOptions,
    ReportStatusOptionsList,
} from '../../utilities/Constants';
import React, { useState } from 'react';
import { Stack, Table } from 'react-bootstrap';

import CheckmateNSelect from '../../components/shared/CheckmateNSelect';
import CheckmateSelect from '../../components/shared/CheckmateSelect';
import CheckmateSelectHelper from '../../utilities/CheckmateSelectHelper';
import Common from '../../stores/Common';
import { DocumentTypeEnum } from '../../utilities/DbConstants';
import { IDocumentSearchModel } from '../../interfaces/IDocument';
import { IMultiSelectOption } from '../../interfaces/ILookup';
import { IReportTypeModel } from '../../interfaces/Report/IReport';
import { LoadingButton } from '../../shared/buttons/LoadingButton';
import { cloneDeep } from 'lodash';
import { useGetReportSubTypes } from '../../shared/react-query-hooks/useGetReportSubTypes';

interface IMyDocumentsSearchFiltersProps {
    currentFilters: IDocumentSearchModel;
    isSearching?: boolean;
    onApplyFilters: (filters: IDocumentSearchModel) => void;
}

const MyDocumentsSearchFilters: React.FC<IMyDocumentsSearchFiltersProps> = (
    props: IMyDocumentsSearchFiltersProps
) => {
    const reportSubTypesApi = useGetReportSubTypes();

    const [localFilters, setLocalFilters] = useState<IDocumentSearchModel>(props.currentFilters);

    //currently hiding the OfficialCaseDocument && WorkProduct
    const staticDocumentTypeFilter = (staticItem: IMultiSelectOption) =>
        staticItem.id !== DocumentTypeEnum.OfficialCaseDocument &&
        staticItem.id !== DocumentTypeEnum.WorkProduct;

    const subTypeToMultiSelectOption = (subType: IReportTypeModel) => {
        return {
            id: subType.id,
            label: subType.displayName,
            value: subType.displayName,
        } as IMultiSelectOption;
    };

    const filterDocumentTypeOptions = DocumentTypeOptions.filter(staticDocumentTypeFilter);
    const filteredReportSubTypeOptions =
        reportSubTypesApi.data?.map(subTypeToMultiSelectOption) ?? [];

    const statusOptions = ReportStatusOptionsList.map((lu) => ({
        id: lu.id,
        value: lu.id.toString(),
        label: lu.displayName ?? '',
    }));

    const handleApplyFiltersClick = () => {
        props.onApplyFilters(localFilters);
    };

    const handleClearFiltersClick = () => {
        setLocalFilters({});
    };

    const handleDocumentTypeChange = (selectedOptions: IMultiSelectOption[]) => {
        const documentSearchModelCopy = cloneDeep(localFilters);
        documentSearchModelCopy.types = selectedOptions;

        const onlyOneTypeIsSelected = (selectedOptions?.length ?? 0) === 1;
        const onlyReportTypeIsSelected =
            onlyOneTypeIsSelected && selectedOptions[0].id === DocumentTypeEnum.Report;

        if (!onlyReportTypeIsSelected) {
            documentSearchModelCopy.subTypes = [];
        }

        setLocalFilters(documentSearchModelCopy);
    };

    const handleReportSubTypeChange = (selectedOptions: IMultiSelectOption[]) => {
        const documentSearchModelCopy = cloneDeep(localFilters);
        const reportTypeModels = reportSubTypesApi.data?.filter((item) =>
            selectedOptions.map((opt) => opt.id).includes(item.id)
        );

        documentSearchModelCopy.subTypes = reportTypeModels;
        setLocalFilters(documentSearchModelCopy);
    };

    const handleStatusChange = (selectedOptions: IMultiSelectOption[]) => {
        const documentSearchModelCopy = cloneDeep(localFilters);
        documentSearchModelCopy.statuses = selectedOptions.map((opt) => opt.id);
        setLocalFilters(documentSearchModelCopy);
    };

    const handleReadChange = (selectedOption: IMultiSelectOption | undefined) => {
        const documentSearchModelCopy = cloneDeep(localFilters);
        const isRead =
            selectedOption?.id === 1 ? true : selectedOption?.id === 0 ? false : undefined;
        documentSearchModelCopy.isRead = isRead;
        setLocalFilters(documentSearchModelCopy);
    };

    const handleDateChange = (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => {
        const { name, value } = event.target;

        const documentSearchModelCopy = cloneDeep(localFilters);
        if (name === 'startDate') {
            const startDate = value;
            if (startDate !== '') {
                documentSearchModelCopy.validationError = Common.isValidDate(startDate)
                    ? ''
                    : 'Invalid Date';
            }
            documentSearchModelCopy.startDate = startDate;
        } else if (name === 'endDate') {
            const endDate = value;
            if (endDate !== '') {
                documentSearchModelCopy.validationError = Common.isValidDate(endDate)
                    ? ''
                    : 'Invalid Date';
            }
            documentSearchModelCopy.endDate = endDate;
        }

        if (documentSearchModelCopy.validationError?.length === 0) {
            setLocalFilters(documentSearchModelCopy);
        }
    };

    const selectedDocumentTypeOptions = filterDocumentTypeOptions.filter((opt) =>
        localFilters.types?.map((t) => t.id).includes(opt.id ?? '')
    );

    const selectedReportSubTypeOptions = filteredReportSubTypeOptions.filter((opt) =>
        localFilters.subTypes?.map((t) => t.id).includes(opt.id ?? '')
    );

    const selectedStatusOptions = statusOptions.filter((opt) =>
        localFilters.statuses?.includes(opt.id ?? '')
    );

    const selectedReadOption = CheckmateSelectHelper.getReadUnreadOptions().find((opt) => {
        let value: number | undefined = undefined;

        if (localFilters.isRead === true) {
            value = 1;
        } else if (localFilters.isRead === false) {
            value = 0;
        }

        return opt.id === value;
    });

    const onlyOneTypeIsSelected = (selectedDocumentTypeOptions?.length ?? 0) === 1;
    const onlyReportTypeIsSelected =
        onlyOneTypeIsSelected && selectedDocumentTypeOptions[0].id === DocumentTypeEnum.Report;

    return (
        <Stack style={{ justifyContent: 'space-between', height: '100%' }}>
            <div style={{ flexGrow: 1 }}>
                <Table borderless className="mt-1">
                    <tbody>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Type</td>
                            <td>
                                <CheckmateNSelect
                                    options={filterDocumentTypeOptions}
                                    value={selectedDocumentTypeOptions}
                                    onChange={handleDocumentTypeChange}
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Sub Type</td>
                            <td>
                                <CheckmateNSelect
                                    options={filteredReportSubTypeOptions}
                                    value={selectedReportSubTypeOptions}
                                    onChange={handleReportSubTypeChange}
                                    disabled={
                                        !onlyReportTypeIsSelected || reportSubTypesApi.isFetching
                                    }
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Status</td>
                            <td>
                                <CheckmateNSelect
                                    options={statusOptions}
                                    value={selectedStatusOptions}
                                    onChange={handleStatusChange}
                                    disabled={!onlyReportTypeIsSelected}
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Read / Unread</td>
                            <td>
                                <CheckmateSelect
                                    options={CheckmateSelectHelper.getReadUnreadOptions()}
                                    value={selectedReadOption}
                                    onChange={handleReadChange}
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Start Date</td>
                            <td>
                                <input
                                    className={
                                        'form-control' +
                                        (localFilters.startDate &&
                                        Common.isValidDate(localFilters.startDate)
                                            ? ''
                                            : ' unselectClass')
                                    }
                                    min="1753-01-01"
                                    disabled={false}
                                    value={Common.safeDateFormat(localFilters.startDate)}
                                    type="date"
                                    name="startDate"
                                    onChange={handleDateChange}
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">End Date</td>
                            <td>
                                <input
                                    className={
                                        'form-control d-inline-block' +
                                        (localFilters.endDate &&
                                        Common.isValidDate(localFilters.endDate)
                                            ? ''
                                            : ' unselectClass')
                                    }
                                    value={Common.safeDateFormat(localFilters.endDate)}
                                    min="1753-01-01"
                                    disabled={false}
                                    type="date"
                                    name="endDate"
                                    onChange={handleDateChange}
                                />
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </div>
            <div style={{ flexShrink: 1, paddingTop: '1.5em', borderTop: '1px solid lightgray' }}>
                <LoadingButton
                    buttonCssClass="btn btn-black"
                    onClick={handleApplyFiltersClick}
                    text="Apply Filters"
                    loadingText="Searching..."
                    loading={props.isSearching}
                />
                <button className="btn btn-default" onClick={handleClearFiltersClick}>
                    Clear Filters
                </button>
            </div>
        </Stack>
    );
};

export default MyDocumentsSearchFilters;
